import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';

import {
    DeliverableCardFile,
    DeliverableCardPayload,
} from '../../../../services/gable/gable.service';

/**
 * Inline rendering of a `build_document` tool result inside the Gable chat — a
 * generated deliverable (board summary .docx/.pdf) shown as a header (blue icon
 * chip + title + subtitle) over a single primary Download button. When more than
 * one format exists the button opens a mat-menu of formats (the app-wide dropdown
 * pattern); a single format downloads directly. Signed URLs render as plain
 * anchors so they open straight from chat.
 *
 * Also supports a `loading` skeleton mode (SD-7240): while the sandbox is
 * still generating the deliverable, the same card shell renders with a
 * pulsing icon circle, the live progress label, and a shimmer bar where the
 * Download button will land — previewing exactly what's coming.
 */
@Component({
    selector: 'app-gable-deliverable-card',
    templateUrl: './deliverable-card.component.html',
    styleUrls: ['./deliverable-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatButtonModule, MatMenuModule],
})
export class GableDeliverableCardComponent {
    @Input() payload: DeliverableCardPayload | null = null;

    /** Skeleton mode — renders the pending-deliverable preview instead of files. */
    @Input() loading = false;
    /** Live progress label shown as the title while loading. */
    @Input() loadingLabel?: string;
    /** Muted expectation hint shown as the subtitle while loading. */
    @Input() loadingHint?: string;

    get title(): string {
        return this.payload?.title ?? 'Document';
    }

    get files(): DeliverableCardFile[] {
        return this.payload?.files ?? [];
    }

    get primaryFile(): DeliverableCardFile | null {
        return this.files[0] ?? null;
    }

    iconFor(format: string): string {
        switch ((format ?? '').toLowerCase()) {
            case 'pdf':
                return 'ph-file-pdf';
            case 'docx':
            case 'doc':
                return 'ph-file-doc';
            case 'xlsx':
            case 'xls':
                return 'ph-file-xls';
            case 'pptx':
            case 'ppt':
                return 'ph-file-ppt';
            case 'csv':
                return 'ph-file-csv';
            default:
                return 'ph-file';
        }
    }

    formatLabel(format: string): string {
        switch ((format ?? '').toLowerCase()) {
            case 'pdf':
                return 'PDF (.pdf)';
            case 'docx':
            case 'doc':
                return 'Word (.docx)';
            case 'xlsx':
            case 'xls':
                return 'Excel (.xlsx)';
            case 'pptx':
            case 'ppt':
                return 'PowerPoint (.pptx)';
            case 'csv':
                return 'CSV (.csv)';
            default:
                return (format ?? '').toUpperCase();
        }
    }

    trackByFormat(_index: number, file: DeliverableCardFile): string {
        return file.format;
    }
}
